/**
 * Display formatting. Every helper takes a nullable number and returns an em
 * dash for null: CLAUDE.md forbids printing a figure that is not traceable to a
 * stored row, so a missing cell has to look missing rather than look like zero.
 */

export const DASH = '—';

export function usd(n: number | null | undefined, digits = 2): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return DASH;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

/** "$2.66M" / "$184K" — the table's liquidity and volume style. */
export function usdCompact(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return DASH;
  const abs = Math.abs(n);
  // Promote when rounding would print `$1000K` or `$1000.00M`. The boundary
  // is the formatted value, not the raw cutoff, so 999_500 is `$1.00M`.
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) {
    const scaled = n / 1e6;
    if (Math.abs(Number(scaled.toFixed(2))) >= 1000) return `$${(n / 1e9).toFixed(2)}B`;
    return `$${scaled.toFixed(2)}M`;
  }
  if (abs >= 1e3) {
    const k = Math.round(n / 1e3);
    if (Math.abs(k) >= 1000) return `$${(n / 1e6).toFixed(2)}M`;
    return `$${k}K`;
  }
  // 999.5 formats as `$1000`. That is the next unit, same as `$1000K`.
  const whole = Number(n.toFixed(0));
  if (Math.abs(whole) >= 1000) return `$${whole < 0 ? -1 : 1}K`;
  return `$${n.toFixed(0)}`;
}

export function count(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return DASH;
  return n.toLocaleString('en-US');
}

/** Signed percentage, always with a sign so a premium reads as a premium. */
export function pct(n: number | null | undefined, digits = 2): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return DASH;
  return `${n >= 0 ? '+' : ''}${n.toFixed(digits)}%`;
}

export function hhmmUtc(ts: number | null | undefined): string {
  if (!ts || !Number.isFinite(ts)) return DASH;
  const d = new Date(ts);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

/** "Sun 03:12 UTC" — the topbar clock and every "as of" stamp. */
export function stampUtc(ts: number = Date.now()): string {
  const d = new Date(ts);
  const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getUTCDay()];
  return `${day} ${hhmmUtc(ts)} UTC`;
}

export function isoUtc(ts: number = Date.now()): string {
  return new Date(ts).toISOString();
}

/**
 * Nine y-coordinates for the 62x20 sparkline in the mockup, plus the direction
 * that colours it. Fewer than two points cannot make a line, so the caller gets
 * `null` and drops the cell.
 */
export interface Spark {
  points: string;
  dir: 'up' | 'down' | 'flat';
}

export function sparkline(values: number[], width = 62, height = 20): Spark | null {
  const clean = values.filter((v) => Number.isFinite(v));
  if (clean.length < 2) return null;
  const lo = Math.min(...clean);
  const hi = Math.max(...clean);
  const span = hi - lo;
  const pad = 2;
  const usable = height - pad * 2;
  const pts = clean.map((v, i) => {
    const x = (i / (clean.length - 1)) * width;
    // A flat series has no span to scale against; park it on the mid-line.
    const y = span === 0 ? height / 2 : pad + (1 - (v - lo) / span) * usable;
    return `${x.toFixed(0)},${y.toFixed(0)}`;
  });
  const first = clean[0]!;
  const last = clean[clean.length - 1]!;
  // A zero start has no relative base, so the 0.1% flat band does not apply.
  // Any move off zero is a direction; only a series that stays at zero is flat.
  const move = first === 0 ? (last === first ? 0 : last > first ? 1 : -1) : (last - first) / Math.abs(first);
  const dir: Spark['dir'] = Math.abs(move) < 0.001 ? 'flat' : move > 0 ? 'up' : 'down';
  return { points: pts.join(' '), dir };
}

/** Even-spaced sample of at most `n` values, keeping the first and the last. */
export function sampleTo<T>(xs: T[], n: number): T[] {
  if (n <= 0) return [];
  if (xs.length <= n) return xs;
  // `n === 1` would divide by zero below and push `undefined`.
  if (n === 1) return [xs[0]!];
  const out: T[] = [];
  for (let i = 0; i < n; i++) out.push(xs[Math.round((i / (n - 1)) * (xs.length - 1))]!);
  return out;
}
